/**
 * @file BinCreateDialog.tsx
 * @description Создание одной ячейки (phase19.14, W14).
 *
 * Формат адреса проверяем до отправки (`isValidBinCode`) — кладовщик должен
 * увидеть ошибку сразу, а не после round-trip. Источник правды остаётся на
 * сервере (422 `bin_code_invalid`).
 */

import { useState } from "react";

import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import { isValidBinCode } from "../../../lib/api-bins";

interface Props {
  warehouseName: string;
  saving: boolean;
  error: string | null;
  onSubmit: (input: { code: string; label?: string }) => void;
  onClose: () => void;
}

const FIELD =
  "min-h-[56px] rounded-xl border border-on-surface/15 bg-surface-lowest px-3 text-base";

export function BinCreateDialog({
  warehouseName,
  saving,
  error,
  onSubmit,
  onClose,
}: Props) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  function submit() {
    const value = code.trim().toUpperCase();
    if (!isValidBinCode(value)) {
      setLocalError(
        "Формат адреса: <стеллаж>-<полка>-<ячейка>, например A-02-03 " +
          "(буква A–Z, числа 1–99).",
      );
      return;
    }
    setLocalError(null);
    onSubmit({ code: value, label: label.trim() || undefined });
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Новая ячейка</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <p className="text-sm text-on-surface-variant">Склад: {warehouseName}</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bin-code">Адрес *</Label>
            <input
              id="bin-code"
              value={code}
              autoFocus
              placeholder="A-02-03"
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setLocalError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submit();
                }
              }}
              disabled={saving}
              className={`${FIELD} font-mono`}
            />
            <span className="text-xs text-on-surface-variant">
              Стеллаж · полка · ячейка. Код уникален в пределах склада и не
              переиспользуется после архивации.
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bin-label">Подпись (опц.)</Label>
            <input
              id="bin-label"
              value={label}
              maxLength={120}
              placeholder="Стеллаж A, полка 2, ячейка 3"
              onChange={(e) => setLabel(e.target.value)}
              disabled={saving}
              className={FIELD}
            />
          </div>
          {localError ?? error ? (
            <Alert severity="error">{localError ?? error}</Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" className="min-h-[56px]" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button className="min-h-[56px]" onClick={submit} disabled={saving}>
            {saving ? "Создаём…" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
